import React from 'react';

import PrimaryButton from '@/components/ui/Button/PrimaryButton';
import RichText from '@/components/ui/RichText/RichText';

import type { ButtonItem, CardLabels } from '@/types/packageCard';

interface CardFooterProps {
  button?: ButtonItem | null;
  onEnquire?: () => void;
  onBook?: () => void;
  priceText?: string | null;
  labels: CardLabels;
  showEnquireAction?: boolean;
}

export default function CardFooter({
  button,
  onEnquire,
  onBook,
  priceText,
  labels,
  showEnquireAction = true,
}: CardFooterProps): React.ReactElement | null {
  const bookLabel = labels.bookLabel?.content ?? 'Book Now';
  const enquireLabel = labels.enquireLabel?.content ?? 'Enquire Now';
  const hasAction = Boolean(button || onBook || showEnquireAction);

  if (!hasAction && !priceText) return null;

  return (
    <div className="mt-auto flex items-center justify-between gap-4 pt-6">
      {button ? (
        <PrimaryButton
          data={{
            title: button.title || bookLabel,
            slug: button.slug,
            buttonType: button.buttonType,
          }}
        />
      ) : onBook ? (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBook}
            className="bg-theme-primary hover:text-theme-primary hover:border-theme-primary font-mulish focus-visible:ring-theme-primary inline-flex cursor-pointer items-center justify-center rounded-lg border-2 border-transparent px-[40px] py-[15px] text-base leading-5 font-normal! tracking-normal text-white transition-colors duration-200 hover:bg-transparent focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-white focus-visible:outline-none"
          >
            {bookLabel}
          </button>
          {showEnquireAction && (
            <button
              type="button"
              onClick={onEnquire}
              aria-label="Send an enquiry about this package"
              className="hover:text-theme-primary hover:border-theme-primary flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="h-5 w-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                />
              </svg>
            </button>
          )}
        </div>
      ) : showEnquireAction ? (
        <PrimaryButton data={{ content: enquireLabel }} onClick={onEnquire} />
      ) : null}

      {priceText && (
        <RichText styles={labels.price?.styles}>{priceText}</RichText>
      )}
    </div>
  );
}
